import { z } from 'zod';

const ARTICLE_HERO_MEDIA_KINDS = ['none', 'demo', 'image', 'video'] as const;
/** Zoom scale: 100 = original size, higher values zoom in. */
const ARTICLE_HERO_MEDIA_ZOOM_LEVELS = ['100', '125', '150', '200'] as const;
const ARTICLE_HERO_MEDIA_ALIGNS = ['left', 'right'] as const;
const ARTICLE_HERO_MEDIA_VERTICAL_ALIGNS = ['top', 'bottom'] as const;

const ArticleHeroMediaKindSchema = z.enum(ARTICLE_HERO_MEDIA_KINDS);
const ArticleHeroMediaSizeSchema = z.enum(ARTICLE_HERO_MEDIA_ZOOM_LEVELS);
const ArticleHeroMediaAlignSchema = z.enum(ARTICLE_HERO_MEDIA_ALIGNS);
const ArticleHeroMediaVerticalAlignSchema = z.enum(ARTICLE_HERO_MEDIA_VERTICAL_ALIGNS);

export type ArticleHeroMediaSize = z.infer<typeof ArticleHeroMediaSizeSchema>;
export type ArticleHeroMediaAlign = z.infer<typeof ArticleHeroMediaAlignSchema>;
export type ArticleHeroMediaVerticalAlign = z.infer<typeof ArticleHeroMediaVerticalAlignSchema>;

export function normalizeArticleHeroMediaSize(value: unknown): ArticleHeroMediaSize {
  if (value === 'original') return '100';
  if (value === 'zoom-out' || value === '25' || value === '50' || value === '75') {
    return '100';
  }
  if (
    typeof value === 'string'
    && (ARTICLE_HERO_MEDIA_ZOOM_LEVELS as readonly string[]).includes(value)
  ) {
    return value as ArticleHeroMediaSize;
  }
  return '100';
}

export const ArticleHeroMediaSchema = z.object({
  kind: ArticleHeroMediaKindSchema,
  demoId: z.string().optional(),
  url: z.string().url().optional(),
  posterUrl: z.string().url().optional(),
  size: z.preprocess(
    (value) => normalizeArticleHeroMediaSize(value),
    ArticleHeroMediaSizeSchema,
  ).default('100'),
  align: ArticleHeroMediaAlignSchema.default('left'),
  verticalAlign: ArticleHeroMediaVerticalAlignSchema.default('top'),
});

export type ArticleHeroMediaKind = z.infer<typeof ArticleHeroMediaKindSchema>;
export type ArticleHeroMedia = z.infer<typeof ArticleHeroMediaSchema>;

export const DEFAULT_ARTICLE_HERO_MEDIA: ArticleHeroMedia = {
  kind: 'none',
  size: '100',
  align: 'left',
  verticalAlign: 'top',
};

export function parseArticleHeroMedia(value: unknown): ArticleHeroMedia | null {
  if (value == null) return null;
  const result = ArticleHeroMediaSchema.safeParse(value);
  return result.success ? result.data : null;
}

export function resolveArticleHeroMedia(input: {
  hero_media?: unknown;
  mini_demo_id?: string | null;
}): ArticleHeroMedia | null {
  const parsed = parseArticleHeroMedia(input.hero_media);
  if (parsed && parsed.kind !== 'none') {
    return parsed;
  }

  if (input.mini_demo_id) {
    return {
      kind: 'demo',
      demoId: input.mini_demo_id,
      size: '100',
      align: 'left',
      verticalAlign: 'top',
    };
  }

  return null;
}

export function normalizeArticleHeroMediaForSave(
  heroMedia: ArticleHeroMedia,
): ArticleHeroMedia | null {
  if (heroMedia.kind === 'none') {
    return null;
  }

  const size = normalizeArticleHeroMediaSize(heroMedia.size);
  const verticalAlign = heroMedia.verticalAlign ?? 'top';

  if (heroMedia.kind === 'demo') {
    if (!heroMedia.demoId) return null;
    return {
      kind: 'demo',
      demoId: heroMedia.demoId,
      size,
      align: heroMedia.align,
      verticalAlign,
    };
  }

  if (!heroMedia.url) return null;

  return {
    kind: heroMedia.kind,
    url: heroMedia.url,
    posterUrl: heroMedia.kind === 'video' ? heroMedia.posterUrl : undefined,
    size,
    align: heroMedia.align,
    verticalAlign,
  };
}

export function getArticleHeroMediaScale(size: ArticleHeroMediaSize | string): number {
  return Number(normalizeArticleHeroMediaSize(size)) / 100;
}

export function getArticleHeroMediaClassName(
  size: ArticleHeroMediaSize | string,
  align: ArticleHeroMediaAlign,
  verticalAlign: ArticleHeroMediaVerticalAlign = 'top',
): string {
  const zoom = normalizeArticleHeroMediaSize(size);
  const alignClass = align === 'left'
    ? 'article-hero-media--align-left'
    : 'article-hero-media--align-right';
  const verticalAlignClass = verticalAlign === 'bottom'
    ? 'article-hero-media--valign-bottom'
    : 'article-hero-media--valign-top';
  return `article-hero-media ${alignClass} ${verticalAlignClass} article-hero-media--zoom-${zoom}`;
}

export function miniDemoIdFromHeroMedia(heroMedia: ArticleHeroMedia): string | null {
  if (heroMedia.kind !== 'demo' || !heroMedia.demoId) {
    return null;
  }
  return heroMedia.demoId;
}
